<template>
  <div ref="wrapper" class="video-call" :class="{ 'video-call--full': isFullscreen, 'video-call--pseudo': pseudoFullscreen }">
    <video
      ref="remote"
      class="video-call__remote"
      autoplay
      playsinline
      v-show="remoteStream"
    ></video>
    <div v-if="!remoteStream" class="video-call__placeholder">
      <v-progress-circular v-if="waiting" indeterminate color="white" class="mb-3"></v-progress-circular>
      <span>{{ status }}</span>
    </div>

    <video
      ref="local"
      class="video-call__local"
      autoplay
      playsinline
      muted
      v-show="localStream"
    ></video>

    <div class="video-call__controls">
      <v-btn fab small :color="micOn ? 'white' : 'error'" :title="micTitle" @click="toggle('audio')">
        <v-icon>{{ micOn ? "mdi-microphone" : "mdi-microphone-off" }}</v-icon>
      </v-btn>
      <v-btn fab small :color="camOn ? 'white' : 'error'" :title="camTitle" @click="toggle('video')">
        <v-icon>{{ camOn ? "mdi-video" : "mdi-video-off" }}</v-icon>
      </v-btn>
      <v-btn fab small color="white" @click="toggleFullscreen">
        <v-icon>{{ isFullscreen ? "mdi-arrow-collapse-all" : "mdi-arrow-expand-all" }}</v-icon>
      </v-btn>
      <slot name="actions"></slot>
    </div>
  </div>
</template>

<script>
export default {
  name: "VideoCall",
  props: {
    localStream: { type: MediaStream, default: null },
    remoteStream: { type: MediaStream, default: null },
    status: { type: String, default: "" },
    waiting: { type: Boolean, default: false },
    micTitle: { type: String, default: "" },
    camTitle: { type: String, default: "" },
  },
  data() {
    return {
      micOn: true,
      camOn: true,
      isFullscreen: false,
      // iPhone Safari has no fullscreen for page elements: the call then covers the screen with CSS
      pseudoFullscreen: false,
    };
  },
  watch: {
    localStream: {
      immediate: true,
      handler(stream) {
        this.$nextTick(() => {
          if (this.$refs.local) this.$refs.local.srcObject = stream;
        });
        this.camOn = !!(stream && stream.getVideoTracks().length);
      },
    },
    remoteStream: {
      immediate: true,
      handler(stream) {
        this.$nextTick(() => {
          if (this.$refs.remote) this.$refs.remote.srcObject = stream;
        });
      },
    },
  },
  mounted() {
    document.addEventListener("fullscreenchange", this.onFullscreenChange);
    document.addEventListener("webkitfullscreenchange", this.onFullscreenChange);
    document.addEventListener("keydown", this.onKeydown);
  },
  beforeDestroy() {
    document.removeEventListener("fullscreenchange", this.onFullscreenChange);
    document.removeEventListener("webkitfullscreenchange", this.onFullscreenChange);
    document.removeEventListener("keydown", this.onKeydown);
    this.setPseudo(false);
  },
  methods: {
    toggle(kind) {
      if (!this.localStream) return;
      const tracks = kind === "audio"
        ? this.localStream.getAudioTracks()
        : this.localStream.getVideoTracks();
      if (!tracks.length) return;

      const enabled = !tracks[0].enabled;
      tracks.forEach((track) => {
        track.enabled = enabled;
      });
      if (kind === "audio") this.micOn = enabled;
      else this.camOn = enabled;
    },
    toggleFullscreen() {
      if (this.pseudoFullscreen) {
        this.setPseudo(false);
        return;
      }
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      if (current) {
        const exit = document.exitFullscreen || document.webkitExitFullscreen;
        if (exit) exit.call(document);
        return;
      }
      const el = this.$refs.wrapper;
      const request = el.requestFullscreen || el.webkitRequestFullscreen;
      if (!request) {
        this.setPseudo(true);
        return;
      }
      try {
        const result = request.call(el);
        if (result && result.catch) result.catch(() => this.setPseudo(true));
      } catch (e) {
        this.setPseudo(true);
      }
    },
    setPseudo(on) {
      this.pseudoFullscreen = on;
      this.isFullscreen = on;
      // the page behind the call must not scroll
      document.documentElement.classList.toggle("video-call-open", on);
    },
    onFullscreenChange() {
      if (this.pseudoFullscreen) return;
      const current = document.fullscreenElement || document.webkitFullscreenElement;
      this.isFullscreen = current === this.$refs.wrapper;
    },
    onKeydown(event) {
      if (this.pseudoFullscreen && event.key === "Escape") this.setPseudo(false);
    },
  },
};
</script>

<style lang="scss" scoped>
.video-call {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  min-height: 240px;
  background: #1f2a33;
  border-radius: 12px;
  overflow: hidden;
}
.video-call--full {
  border-radius: 0;
}
.video-call--pseudo {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 10000;
  margin: 0 !important;
  width: 100vw;
  height: 100vh;
  height: 100dvh;
  min-height: 0;
  aspect-ratio: auto;
  padding-bottom: env(safe-area-inset-bottom);
}
.video-call__remote {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background: #000;
}
.video-call__placeholder {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 16px;
  color: #fff;
  text-align: center;
}
.video-call__local {
  position: absolute;
  right: 12px;
  top: 12px;
  width: 26%;
  max-width: 220px;
  min-width: 96px;
  aspect-ratio: 4 / 3;
  object-fit: cover;
  border-radius: 8px;
  border: 2px solid rgba(255, 255, 255, 0.7);
  background: #000;
  transform: scaleX(-1);
}
.video-call__controls {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 12px;
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px;
  padding: 0 8px;
}
</style>

<style>
html.video-call-open,
html.video-call-open body {
  overflow: hidden !important;
}
</style>
